import React, { useState, useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { fetchOperationsData } from '../../store/slices/operationsSlice';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Modal } from '../../components/ui/modal';
import TransactionReceipt from '../../components/transactions/TransactionReceipt';
import { PROVIDERS } from '../../lib/constants';
import { Method, Invoice, operationsService } from '../../services/api/operationsService';
import { friendlyError } from '../../services/api/config';
import { addTransaction } from '../../store/slices/transactionsSlice';
import { printService } from '../../services/printService';
import { Transaction } from '../../types';

const STORAGE_BASE = 'https://kaoxet.net/storage/';
const ACCENT = '#10b981';

const fmt = (n: number) =>
  new Intl.NumberFormat('es-DO', { style: 'currency', currency: 'DOP', minimumFractionDigits: 2 }).format(n);

type PayOption = 'min' | 'total';

const FacturasPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { wallet }         = useAppSelector((state) => state.wallet as any);
  const { invoiceMethods } = useAppSelector((state) => state.operations as any);
  const fetchedRef = useRef(false);

  const [serviceNumber,     setServiceNumber]    = useState('');
  const [selectedProvider,  setSelectedProvider] = useState('');
  const [invoice,           setInvoice]          = useState<Invoice | null>(null);
  const [payOption,         setPayOption]        = useState<PayOption>('total');
  const [isLoading,         setIsLoading]        = useState(false);
  const [isProcessing,      setIsProcessing]     = useState(false);
  const [error,             setError]            = useState<string | null>(null);
  const [success,           setSuccess]          = useState<string | null>(null);
  const [showReceiptModal,  setShowReceiptModal]  = useState(false);
  const [currentTransaction, setCurrentTransaction] = useState<Transaction | null>(null);

  useEffect(() => {
    if (!fetchedRef.current && (!invoiceMethods || invoiceMethods.length === 0)) {
      fetchedRef.current = true;
      dispatch(fetchOperationsData());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const providerList: Method[] =
    invoiceMethods && invoiceMethods.length > 0 ? invoiceMethods : PROVIDERS.FACTURAS;

  const handleProviderSelect = (id: string) => {
    setSelectedProvider(id);
    setInvoice(null); setPayOption('total'); setError(null);
  };

  const consultarFactura = async () => {
    if (!serviceNumber) { setError('Ingrese el número de servicio'); return; }
    if (!selectedProvider) { setError('Seleccione un proveedor'); return; }
    setIsLoading(true); setError(null); setInvoice(null);
    try {
      const res = await operationsService.fetchInvoices(serviceNumber, selectedProvider);
      if (!res.success) throw new Error(friendlyError(res.message || 'Error al consultar la factura'));
      const raw: any[] = Array.isArray(res.data) ? res.data : (res.data ? [res.data] : []);
      if (raw.length === 0) { setError('No se encontró factura para el número de servicio ingresado'); return; }
      // Normalizar campos: el API puede devolver 'name' en lugar de 'client_name'
      const list: Invoice[] = raw.map((inv: any) => ({
        ...inv,
        client_name:     inv.client_name || inv.name || '',
        address:         inv.address || '',
        number_invoices: inv.number_invoices ?? null,
        _status: (() => {
          const s = String(inv.state ?? '').toUpperCase();
          if (s === 'L' || s === 'A') return 'Activo';
          if (s === 'S')              return 'Suspendido';
          if (s === 'C')              return 'Cortado';
          if (s === 'V')              return 'Vencido';
          return inv.state || 'Activo';
        })(),
        _statusColor: (() => {
          const s = String(inv.state ?? '').toUpperCase();
          if (s === 'L' || s === 'A') return 'green';
          return 'red';
        })(),
      }));
      setInvoice(list[0]);
      // si no hay mínimo disponible, fijar opción en total
      setPayOption((list[0].amount_min ?? 0) > 0 ? 'total' : 'total');
    } catch (err: any) {
      setError(friendlyError(err.message || 'Error al consultar la factura. Intente nuevamente.'));
    } finally {
      setIsLoading(false);
    }
  };

  const amountToPay = invoice
    ? (payOption === 'min' ? (invoice.new_min_total ?? invoice.amount_min ?? 0) : (invoice.new_total ?? invoice.total))
    : 0;

  const hasMin = invoice && (invoice.amount_min ?? 0) > 0;

  const handlePayment = async () => {
    if (!invoice) return;
    if (wallet && wallet.balance < amountToPay) { setError('Saldo insuficiente para realizar este pago'); return; }
    setIsProcessing(true); setError(null);
    try {
      const res = await operationsService.sendInvoice(
        amountToPay, serviceNumber,
        invoice.client_name || selectedProvider,
        selectedProvider,
        String(invoice.id)
      );
      if (!res.success) throw new Error(friendlyError(res.message || 'Error al procesar el pago'));
      const providerName = providerList.find(p => p.id === selectedProvider)?.name || selectedProvider;
      const transaction: any = {
        id:        res.data?.xid || res.data?.transactionId || `fact-${Date.now()}`,
        xid:       res.data?.xid || res.data?.transactionId || undefined,
        type:      'FACTURA' as const,
        amount:    amountToPay,
        provider:  providerName,
        date:      new Date().toISOString(),
        status:    'COMPLETED' as const,
        reference: Math.random().toString(36).substring(2, 10).toUpperCase(),
        url:       res.data?.url || null,
        details: {
          serviceNumber, provider: providerName,
          amount: amountToPay,
          paymentType: payOption === 'min' ? 'Pago Mínimo' : 'Pago Total',
          customerName: invoice.client_name,
        },
      };
      dispatch(addTransaction(transaction));
      setSuccess(`Pago de ${fmt(amountToPay)} realizado con éxito`);
      setCurrentTransaction(transaction);
      setShowReceiptModal(true);
      setServiceNumber(''); setSelectedProvider(''); setInvoice(null); setPayOption('total');
    } catch (err: any) {
      setError(friendlyError(err.message || 'Error al procesar el pago. Intente nuevamente.'));
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-6 max-w-3xl">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
          <svg className="w-6 h-6 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
          </svg>
          Pago de Facturas
        </h1>
        <p className="text-slate-500 text-sm mt-1">Pague facturas de servicios básicos y otros.</p>
      </div>

      {/* Alerts */}
      {error && (
        <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-4 mb-5">
          <svg className="w-5 h-5 text-red-500 shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"/>
          </svg>
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}
      {success && (
        <div className="flex items-start gap-3 bg-green-50 border border-green-200 rounded-xl p-4 mb-5">
          <svg className="w-5 h-5 text-green-500 shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
          </svg>
          <p className="text-sm text-green-700">{success}</p>
        </div>
      )}

      <div className="space-y-5">

        {/* ── 1. Proveedor ───────────────────────────────────────────────────── */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5">
          <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">Proveedor / Servicio</p>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
            {providerList.map((p: Method) => (
              <button key={p.id} type="button" onClick={() => handleProviderSelect(p.id)}
                className="flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition-all duration-150 hover:border-emerald-300"
                style={{
                  borderColor: selectedProvider === p.id ? ACCENT : '#e2e8f0',
                  background:  selectedProvider === p.id ? '#ecfdf5' : '#fff',
                }}>
                <div className="w-12 h-12 rounded-full bg-slate-50 flex items-center justify-center overflow-hidden">
                  {p.logo
                    ? <img src={`${STORAGE_BASE}${p.logo}`} alt={p.name} className="w-10 h-10 object-contain"/>
                    : <span className="text-lg font-bold text-slate-400">{p.name.charAt(0)}</span>
                  }
                </div>
                <span className="text-xs font-medium text-slate-600 text-center leading-tight">{p.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* ── 2. Número de servicio ──────────────────────────────────────────── */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5">
          <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">Número de servicio</p>
          <div className="flex gap-3">
            <div className="flex-1">
              <Input
                label="Número de cuenta / contrato"
                value={serviceNumber}
                onChange={e => { setServiceNumber(e.target.value); setInvoice(null); setError(null); }}
                placeholder="Ej: 12345678" fullWidth
                useCustomKeyboard={false}
              />
            </div>
            <div className="flex items-end">
              <Button type="button" onClick={consultarFactura} isLoading={isLoading}
                disabled={!serviceNumber || !selectedProvider}>
                {isLoading ? '...' : 'Consultar'}
              </Button>
            </div>
          </div>
        </div>

        {/* ── 3. Detalle de factura ──────────────────────────────────────────── */}
        {invoice && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
            {/* Header de la factura */}
            <div className="flex items-center gap-4 px-5 py-4 border-b border-slate-100"
              style={{ background: 'linear-gradient(135deg,#ecfdf5,#f0fdf4)' }}>
              {/* Logo del proveedor */}
              {(() => {
                const prov = providerList.find(p => p.id === selectedProvider);
                return prov?.logo ? (
                  <img src={`${STORAGE_BASE}${prov.logo}`} alt={prov.name}
                    className="w-12 h-12 rounded-full object-contain bg-white p-1 shadow-sm"/>
                ) : (
                  <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                    <span className="text-lg font-bold text-emerald-600">
                      {providerList.find(p => p.id === selectedProvider)?.name.charAt(0)}
                    </span>
                  </div>
                );
              })()}
              <div className="flex-1 min-w-0">
                <p className="font-bold text-slate-800 text-lg truncate">{invoice.client_name || 'Cliente'}</p>
                <p className="text-xs text-slate-500">N° Servicio: {serviceNumber}</p>
                {invoice.address && (
                  <p className="text-xs text-slate-400 truncate mt-0.5">{invoice.address}</p>
                )}
                {(invoice.number_invoices ?? 0) > 0 && (
                  <p className="text-xs text-amber-600 font-medium mt-0.5">
                    {invoice.number_invoices} factura{(invoice.number_invoices ?? 0) > 1 ? 's' : ''} pendiente{(invoice.number_invoices ?? 0) > 1 ? 's' : ''}
                  </p>
                )}
              </div>
              {(invoice as any)._status && (
                <span className={`text-xs font-bold px-2.5 py-1 rounded-full shrink-0 ${
                  (invoice as any)._statusColor === 'green'
                    ? 'bg-green-100 text-green-700'
                    : 'bg-red-100 text-red-600'
                }`}>
                  {(invoice as any)._status}
                </span>
              )}
            </div>

            {/* Detalle de montos */}
            <div className="divide-y divide-slate-100">
              {/* Sección pago mínimo */}
              {hasMin && (
                <>
                  <div className="px-5 py-2 bg-slate-50">
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Pago Mínimo</p>
                  </div>
                  <div className="flex justify-between items-center px-5 py-2.5">
                    <span className="text-sm text-slate-500">Monto mínimo</span>
                    <span className="text-sm font-semibold text-slate-700">{fmt(invoice.amount_min ?? 0)}</span>
                  </div>
                  {(invoice.min_service_charge ?? 0) > 0 && (
                    <div className="flex justify-between items-center px-5 py-2.5">
                      <span className="text-sm text-slate-500">Cargo servicio mínimo</span>
                      <span className="text-sm font-semibold text-slate-700">{fmt(invoice.min_service_charge ?? 0)}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center px-5 py-2.5 bg-emerald-50">
                    <span className="text-sm font-bold text-emerald-700">Total mínimo</span>
                    <span className="text-base font-black text-emerald-600">{fmt(invoice.new_min_total ?? (invoice.amount_min ?? 0))}</span>
                  </div>
                </>
              )}

              {/* Sección pago total */}
              <div className="px-5 py-2 bg-slate-50">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Pago Total</p>
              </div>
              <div className="flex justify-between items-center px-5 py-2.5">
                <span className="text-sm text-slate-500">Monto total</span>
                <span className="text-sm font-semibold text-slate-700">{fmt(invoice.total)}</span>
              </div>
              {(invoice.service_charge ?? 0) > 0 && (
                <div className="flex justify-between items-center px-5 py-2.5">
                  <span className="text-sm text-slate-500">Cargo servicio</span>
                  <span className="text-sm font-semibold text-slate-700">{fmt(invoice.service_charge ?? 0)}</span>
                </div>
              )}
              <div className="flex justify-between items-center px-5 py-2.5 bg-emerald-50">
                <span className="text-sm font-bold text-emerald-700">Total a pagar</span>
                <span className="text-base font-black text-emerald-600">{fmt(invoice.new_total ?? invoice.total)}</span>
              </div>
            </div>
          </div>
        )}

        {/* ── 4. Opciones de pago ────────────────────────────────────────────── */}
        {invoice && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5">
            <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">Seleccione una opción de pago</p>
            <div className={`grid gap-3 ${hasMin ? 'grid-cols-2' : 'grid-cols-1'}`}>
              {hasMin && (
                <button type="button" onClick={() => setPayOption('min')}
                  className="p-4 rounded-2xl border-2 text-center transition-all duration-150"
                  style={{
                    borderColor: payOption === 'min' ? ACCENT : '#e2e8f0',
                    background:  payOption === 'min' ? '#ecfdf5' : '#fff',
                  }}>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Pago Mínimo</p>
                  {(invoice.min_service_charge ?? 0) > 0 && (
                    <p className="text-xs text-slate-400 mb-1">
                      + {fmt(invoice.min_service_charge ?? 0)} cargo
                    </p>
                  )}
                  <p className="text-xl font-black" style={{ color: payOption === 'min' ? ACCENT : '#64748b' }}>
                    {fmt(invoice.new_min_total ?? (invoice.amount_min ?? 0))}
                  </p>
                </button>
              )}
              <button type="button" onClick={() => setPayOption('total')}
                className="p-4 rounded-2xl border-2 text-center transition-all duration-150"
                style={{
                  borderColor: payOption === 'total' ? ACCENT : '#e2e8f0',
                  background:  payOption === 'total' ? '#ecfdf5' : '#fff',
                }}>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Pago Total</p>
                {(invoice.service_charge ?? 0) > 0 && (
                  <p className="text-xs text-slate-400 mb-1">
                    + {fmt(invoice.service_charge ?? 0)} cargo
                  </p>
                )}
                <p className="text-xl font-black" style={{ color: payOption === 'total' ? ACCENT : '#64748b' }}>
                  {fmt(invoice.new_total ?? invoice.total)}
                </p>
              </button>
            </div>
          </div>
        )}

        {/* ── 5. Botón pagar ─────────────────────────────────────────────────── */}
        {invoice && (
          <Button type="button" size="touch" fullWidth isLoading={isProcessing} onClick={handlePayment}>
            {isProcessing ? 'Procesando...' : `✓ Pagar ${fmt(amountToPay)}`}
          </Button>
        )}
      </div>

      <Modal isOpen={showReceiptModal} onClose={() => setShowReceiptModal(false)}
        title="Resumen de Transacción" size="md">
        {currentTransaction && (
          <TransactionReceipt transaction={currentTransaction}
            onClose={() => setShowReceiptModal(false)}
            onPrint={() => { if (currentTransaction) printService.printTicket(currentTransaction); }}
          />
        )}
      </Modal>
    </div>
  );
};

export default FacturasPage;
